import { Card, CardBody } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, StatusPill, type StatusTone } from '@vnetwork/ui/data';
import type { InvoiceDetail } from '@/api/invoices';
import { resolveSaleType } from '@/pages/invoices/invoice-format';

/** Map `invoice_sale_type` code → display label. */
const SALE_TYPE_LABELS: Record<string, string> = {
  renewable: 'Renewable',
  'new-sell': 'New Sell',
  upsell: 'Upsell',
};

/** Map `invoice_sale_type` code → @ui StatusPill tone. */
function saleTypeTone(code: string): StatusTone {
  switch (code) {
    case 'renewable':
      return 'success';
    case 'new-sell':
      return 'info';
    case 'upsell':
      return 'warning';
    default:
      return 'neutral';
  }
}

/**
 * Commission section — an inline flat Card band between `InvoiceInfoCard` and
 * the tab strip. Shown only when the invoice has commission employee data or a
 * sale type. Mirrors `invoice-totals-card.tsx` (flat card + StatGroup).
 *
 * Fields come from `GET /invoices/:id` (no extra endpoint):
 * - `employee_names` — comma-joined names string (e.g. "Alice, Bob")
 * - `employee_codes` — comma-joined codes string (e.g. "ALC, BOB")
 * - `invoice_sale_type` — `{ invoice_sale_type, valid }` wrapper on detail endpoint
 */
export function InvoiceCommissionSection({ detail }: { detail: InvoiceDetail }) {
  const saleTypeCode = resolveSaleType(detail.invoice_sale_type);
  const hasEmployees = !!detail.employee_names;
  const hasSaleType = !!saleTypeCode;

  if (!hasEmployees && !hasSaleType) return null;

  return (
    <Card flat>
      <CardBody>
        <StatGroup columns={3}>
          <Stat
            label="Commission Employees"
            value={detail.employee_names || '—'}
          />
          <Stat
            label="Employee Codes"
            value={detail.employee_codes || '—'}
          />
          <Stat
            label="Sale Type"
            value={
              saleTypeCode ? (
                <StatusPill tone={saleTypeTone(saleTypeCode)}>
                  {SALE_TYPE_LABELS[saleTypeCode] ?? saleTypeCode}
                </StatusPill>
              ) : (
                '—'
              )
            }
          />
        </StatGroup>
      </CardBody>
    </Card>
  );
}
